import styled from "styled-components/macro";
import { formatDateTime } from "../../utils/textUtils";

// Mirrors the same cutoff applied in the webapp at sync time and in the client-app backend at
// response build (maxSnippetAge). Kept here as the last line: it retires the snippet on a
// dashboard left open between the hourly refetches.
const MAX_SNIPPET_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// The agent-written progress update, opening the live update panel above the deterministic status
// it summarises. The snippet is already resolved for the requesting audience by the backend, so
// this component is audience-agnostic. Every guard fails closed: without a usable snippet it
// renders nothing and the panel is exactly what it is today.
//
// No font size is set: the snippet takes its surface's body size (the estate agent TabPanel's
// `*` rule, the client DashboardCard's content), so it sits among the status rows rather than
// above them.
function ProgressUpdate({ progressUpdate }) {
  const snippet = progressUpdate?.snippet?.trim();
  const triggeredAt = progressUpdate?.triggered_at;

  if (!snippet || triggeredAt == null) {
    return null;
  }

  const triggeredAtMs = new Date(triggeredAt).getTime();
  if (Number.isNaN(triggeredAtMs) || Date.now() - triggeredAtMs > MAX_SNIPPET_AGE_MS) {
    return null;
  }

  return (
    // The region label tells a screen reader the content is generated before it hears the prose.
    <Update role="region" aria-label="Automatically generated progress update">
      {sentences(snippet).map((sentence) => (
        <Sentence key={sentence}>{sentence}</Sentence>
      ))}
      <Provenance>
        Automatically generated: <time dateTime={triggeredAt}>{formatDateTime(triggeredAt)}</time>
      </Provenance>
    </Update>
  );
}

export default ProgressUpdate;

// The agent writes one sentence per line (app-support-agent#1628). Each becomes its own paragraph
// with a half-line gap, rather than a run-on paragraph or full paragraph breaks.
function sentences(snippet) {
  return snippet
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

// `&&` doubles the class purely for specificity: TabPanel sets `* { font-size }` and
// `ul, p { margin }` on its contents, which beat a single styled-components class.
const Update = styled.div`
  && {
    width: 100%;
    border-bottom: 1px solid var(--juno-color-background-grey);

    padding-bottom: var(--juno-space-3);
    margin-bottom: var(--juno-space-4);
  }
`;

// One paragraph per sentence, with a half-line gap between them: enough to separate them, not a
// full paragraph break.
const Sentence = styled.p`
  && {
    font-weight: var(--juno-font-weight-regular);
    color: var(--juno-color-text);

    /* Agent prose: force long unbroken strings (references, URLs) to wrap. */
    overflow-wrap: break-word;

    margin-top: 0;
    margin-bottom: var(--juno-space-1);
  }

  /* Adjacent margins collapse, so this is the whole gap between sentences; the last sentence
     keeps the tighter gap to the provenance line. */
  && + && {
    margin-top: var(--juno-space-2);
  }
`;

// Matches CaseUpdateInfo ("Most recent update") at the foot of the estate agent panel — change
// them together.
const Provenance = styled.p`
  && {
    color: var(--juno-color-text-secondary);
    line-height: 1.5;

    margin-top: 0;
    margin-bottom: var(--juno-space-1);
  }
`;
